/*
  Сверка поступлений за месяц - §3.10.4.

  Отвечает на три вопроса о деньгах: сколько пришло, сколько система разнесла
  сама и сколько ждёт разбора. Ниже - отчёт «что сработало» (§3.10.3): какое
  воздействие принесло деньги. Он считался движком с 05.09.2026, но не
  показывался нигде, и клиент не видел, ради чего платит.

  **Месяц без поступлений показывается пустым, а не нулями.** §3.1.4: величина,
  которую не измеряли, нулём не показывается - «вернули 0 ₽» читается как
  «должники не платят», а означает «выписку за этот месяц не загружали».
*/

import { money, monthTitle, percent, plural } from '../api/format.js'
import { Empty, Loading, MoneyCard, Notice } from './Common.jsx'
import PageBar from './PageBar.jsx'
import usePaging from './usePaging.js'

const payments = (count) => plural(count, 'платёж', 'платежа', 'платежей')
const lines = (count) => plural(count, 'строка', 'строки', 'строк')

/*
  Форма слова БЕЗ числа - для глагола, согласованного с подлежащим, у которого
  число уже стоит: «1 строка подтвердила», «3 строки подтвердили». Правило
  числа одно, из `plural`; 16.10.2026 первая правка подставила `plural` целиком
  и экран показал «3 строки выписки 3 подтвердили» - тест это поймал.
*/
const agree = (count, one, other) =>
  plural(count, one, other, other).endsWith(one) ? one : other

/*
  Выписка банка - сверочная, а не второй источник денег (решение владельца
  14.10.2026, §3.10.5). Строка выписки, совпавшая с платежом из 1С, его
  подтверждает и в «Поступило» не входит - об этом сказано спокойно. Строка,
  которой в 1С нет, - вопрос к бухгалтеру: деньги банк видел, а оплата не
  проведена. Её показываем предупреждением.
*/
function OneSourceOfMoney({ summary }) {
  const missing = summary.missing_in_accounting_count ?? 0
  const confirmed = summary.confirmed_count ?? 0
  if (!missing && !confirmed) return null
  return (
    <section className="section">
      {missing > 0 && (
        <Notice kind="warn">
          <div className="notice__what">
            В выписке банка {lines(missing)} на{' '}
            {money(summary.missing_in_accounting_total)}, которых нет в 1С.
          </div>
          <div className="notice__do">
            Деньги пришли, а оплата в учётной системе не проведена. Проверьте у бухгалтера: после
            проведения платёж придёт ночным обменом, и строка выписки станет его подтверждением.
          </div>
        </Notice>
      )}
      {confirmed > 0 && (
        <p className="muted">
          {lines(confirmed)} выписки {agree(confirmed, 'подтвердила', 'подтвердили')} платежи из 1С и
          второй раз {agree(confirmed, 'не посчитана', 'не посчитаны')}.
        </p>
      )}
    </section>
  )
}

export default function Reconciliation({ summary, month }) {
  // 21.10.2026: общая панель страниц (замечание владельца) и под короткой таблицей причин.
  // Причины - расчёт сервера по платежам месяца (`summary.by_cause`), своей точки со
  // страницами у них нет: панель листает список в браузере. Хук - до раннего return.
  const { rows: causes, bar } = usePaging(summary?.by_cause)
  if (!summary) return <Loading what="Считаем сверку за месяц" />

  if (!summary.has_data) {
    return (
      <Empty title={`За ${monthTitle(month)} поступлений не загружали`}>
        Загрузите банковскую выписку или реестр оплат в разделе «Загрузка данных», вкладка
        «Поступления». До этого «Возвращено» показать не из чего: ноль здесь означал бы, что
        должники не платят, а это не измерялось.
      </Empty>
    )
  }

  const matchedShare = Number(summary.received_total)
    ? Number(summary.matched_total) / Number(summary.received_total)
    : null

  return (
    <>
      {/* `grid` - тот же ряд карточек, что на дашборде. Класса `cards` в
          оформлении нет, и с ним три карточки вставали столбиком во весь
          экран (найдено осмотром 10.09.2026). */}
      <section className="section grid">
        <MoneyCard
          label="Поступило от должников"
          value={summary.received_total}
          note={payments(summary.received_count)}
          short={false}
        />
        <MoneyCard
          label="Разнесено автоматически"
          value={summary.matched_total}
          note={
            matchedShare === null
              ? 'считать не от чего'
              : `${payments(summary.matched_count)}, ${percent(matchedShare)}`
          }
          short={false}
        />
        <MoneyCard
          label="Требует разбора"
          value={summary.needs_review_total}
          note={payments(summary.needs_review_count)}
          short={false}
        />
      </section>

      <OneSourceOfMoney summary={summary} />

      <section className="section">
        <h2 className="section__title">Что принесло деньги</h2>
        {summary.by_cause.length === 0 ? (
          <Empty title="Причины платежей ещё не определены">
            Причина появляется, когда поступление связано со счётом: Арбитр ЖКХ смотрит, что было с
            должником до платежа.
          </Empty>
        ) : (
          <div className="card">
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Что сработало</th>
                    <th className="table__num">Принесло</th>
                  </tr>
                </thead>
                <tbody>
                  {causes.map((item) => (
                    <tr key={item.cause}>
                      <td>{item.title}</td>
                      <td className="table__num" data-label="Принесло">
                        {money(item.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PageBar {...bar} what="причин" />
          </div>
        )}
      </section>
    </>
  )
}
